import "./UrlonSectionView.styl";
import { createJSONEditor } from "vanilla-jsoneditor/standalone.js";

// ---------------------------------------------------------------------------
// URLON detection & extraction helpers
// ---------------------------------------------------------------------------

function extractUrlon(raw) {
  const s = raw.trim();

  // Full URL
  if (/^https?:\/\//.test(s)) {
    // Hash fragment takes priority (URLON3 hash state)
    const hashIdx = s.indexOf("#");
    if (hashIdx !== -1) {
      const fragment = s.slice(hashIdx + 1);
      const version = fragment.startsWith("$") ? 3 : 2;
      return { version, urlonPart: fragment, prefix: s.slice(0, hashIdx + 1), suffix: "" };
    }
    // Query string
    const qIdx = s.indexOf("?");
    if (qIdx !== -1) {
      const afterQ = s.slice(qIdx + 1);
      const hashIdx2 = afterQ.indexOf("#");
      const queryPart = hashIdx2 !== -1 ? afterQ.slice(0, hashIdx2) : afterQ;
      const suffix = hashIdx2 !== -1 ? afterQ.slice(hashIdx2) : "";
      const version = queryPart.startsWith("$") ? 3 : 2;
      return { version, urlonPart: queryPart, prefix: s.slice(0, qIdx + 1), suffix };
    }
    return null;
  }

  // Bare URLON string
  const version = s.startsWith("$") ? 3 : 2;
  return { version, urlonPart: s, prefix: "", suffix: "" };
}

function parseUrlon(urlonPart, version) {
  if (version === 3) return window.urlon.parse(urlonPart);
  return window.urlon2.parse(urlonPart);
}

function stringifyUrlon(obj, version) {
  if (version === 3) return window.urlon.stringify(obj);
  return window.urlon2.stringify(obj);
}

// ---------------------------------------------------------------------------
// View
// ---------------------------------------------------------------------------

export default function UrlonSectionView({ dom }) {
  const container = dom;
  container.classed("urlonSection", true);

  const sectionHeader = container.append("div").class("admin-section-header");
  sectionHeader.append("h1").text("URL Object Notation");
  const actionsBox = sectionHeader.append("div").class("actionsbox");
  const versionBadge = actionsBox.append("div").class("urlon-version-badge");
  const parseBtn = actionsBox.append("button")
    .class("button bigger urlon-parse-btn")
    .text("Parse");
  const stringifyBtn = actionsBox.append("button")
    .class("button bigger urlon-stringify-btn")
    .text("Stringify")
    .attr("disabled", true);

  const layout = container.append("div").class("urlon-layout");

  // Left column ──────────────────────────────────────────────────────────────

  const leftCol = layout.append("div").class("urlon-left");

  const leftLabelRow = leftCol.append("div").class("urlon-label-row");
  leftLabelRow.append("label").class("urlon-label").text("String");
  leftLabelRow.append("label").class("urlon-examples").text("Examples:");

  const EXAMPLES = [
    { label: "v3 state",  value: "http://localhost:4200/#$ui$locale$id=en;;&model$markers$bubble$data$filter$dimensions$geo$/$or@$is--kommun:true;&$kommun$/$in@=0180;;&is--regso:true;;&/$nor@$geo$/$in@=0180;;&is--kommun:true;;;;;;&encoding$y$scale$zoomed@:170.51&:1030.75;;;&x$scale$zoomed@:0.24&:0.8;;;;;;;&chart-type=bubbles&url=v2" },
    { label: "v2 query", value: "https://small-waffle.gapminder.org/v2/fasttrack/master/4e9f1e4?_language=en&select_key@=geo&=time;&value@=pop&=lex&=gdp/_pcap;;&from=datapoints&where_geo=$geo;&join_$geo_key=geo&where_$or@_un/_state:true" },
    { label: "v3 bare",  value: "$language=en&select$key@=geo&=time;&value@=pop&=lex&=gdp_pcap;;&from=datapoints&where$/$and@$time=2024;&$geo=$geo;;;&join$/$geo$key=geo&where$/$or@$un_state:true" },
  ];
  EXAMPLES.forEach(({ label, value }) => {
    leftLabelRow.append("a")
      .class("urlon-example-link")
      .text(label)
      .attr("href", "#")
      .on("click", (event) => {
        event.preventDefault();
        textBox.node().value = value;
        parseBtn.dispatch("click");
      });
  });

  const textBox = leftCol.append("textarea")
    .class("input urlon-input")
    .attr("placeholder", "Paste a full URL or a bare URLON string here…")
    .attr("value", "$;");

  // Right column ─────────────────────────────────────────────────────────────

  const rightCol = layout.append("div").class("urlon-right");

  rightCol.append("label").class("urlon-label").text("JSON");

  const editorHost = rightCol.append("div").class("urlon-editor").node();

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  let editorState = null; // { version, prefix, suffix }
  let jsonEditor = null;

  function isLightTheme() {
    return document.documentElement.getAttribute("data-theme") === "light";
  }

  function updateEditorTheme() {
    d3.select(editorHost).classed("jse-theme-dark", !isLightTheme());
  }

  // Keep the editor theme in sync when the user toggles light/dark
  const themeObserver = new MutationObserver(updateEditorTheme);
  themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });

  function initEditor() {
    if (jsonEditor) {
      jsonEditor.destroy();
      jsonEditor = null;
    }
    jsonEditor = createJSONEditor({
      target: editorHost,
      props: {
        content: { json: {} },
        onChange: (content) => {
          const hasText = content?.text != null && content.text.trim() !== "";
          const hasJson = content?.json !== null && content?.json !== undefined;
          stringifyBtn.attr("disabled", (hasText || hasJson) && editorState ? null : true);
        }
      }
    });
  }

  // Show an empty editor immediately on load
  initEditor();
  updateEditorTheme();
  editorState = { version: 3, prefix: "", suffix: "" };
  stringifyBtn.attr("disabled", null);

  // ---------------------------------------------------------------------------
  // Parse
  // ---------------------------------------------------------------------------

  textBox.on("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      parseBtn.dispatch("click");
    }
  });

  parseBtn.on("click", () => {
    const raw = textBox.node().value;
    if (!raw.trim()) return;

    versionBadge.classed("urlon-error", false).text("");

    const extracted = extractUrlon(raw);
    if (!extracted) {
      versionBadge.text("Could not detect URLON in input").classed("urlon-error", true);
      return;
    }

    const { urlonPart, prefix, suffix } = extracted;
    let { version } = extracted;
    let parsed;

    try {
      parsed = parseUrlon(urlonPart, version);
    } catch (e) {
      // Try the other version as fallback
      const alt = version === 3 ? 2 : 3;
      try {
        parsed = parseUrlon(urlonPart, alt);
        version = alt;
      } catch (e2) {
        versionBadge
          .text(`Parse error: ${e.message}`)
          .classed("urlon-error", true);
        return;
      }
    }

    editorState = { version, prefix, suffix };
    versionBadge.text(`URLON${version} ✓ parsed`);

    initEditor();
    updateEditorTheme();
    jsonEditor.set({ json: parsed });
    stringifyBtn.attr("disabled", null);
  });

  // ---------------------------------------------------------------------------
  // Stringify
  // ---------------------------------------------------------------------------

  stringifyBtn.on("click", () => {
    if (!editorState || !jsonEditor) return;

    let currentJson;
    try {
      const content = jsonEditor.get();
      if (content.json !== undefined && content.json !== null) {
        currentJson = content.json;
      } else if (content.text !== undefined && content.text.trim() !== "") {
        currentJson = JSON.parse(content.text);
      } else {
        versionBadge.text("Nothing to stringify").classed("urlon-error", true);
        return;
      }
    } catch (e) {
      versionBadge.text(`Invalid JSON: ${e.message}`).classed("urlon-error", true);
      return;
    }

    let stringified;
    try {
      stringified = stringifyUrlon(currentJson, editorState.version);
    } catch (e) {
      versionBadge.text(`Stringify error: ${e.message}`).classed("urlon-error", true);
      return;
    }

    textBox.node().value = editorState.prefix + stringified + editorState.suffix;
    versionBadge
      .classed("urlon-error", false)
      .text(`URLON${editorState.version} ✓ stringified`);
  });

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  function render() {
    // No server-specific data needed; nothing to do
  }

  function renderError(msg) {
    versionBadge.text(msg).classed("urlon-error", true);
  }

  return { render, renderError };
}
